import { useMemo } from 'react';
import { UserAnalytics } from 'app/shared/types/user-analytics.types';
import { detect } from 'detect-browser';
import { User } from 'app/data/types/user';
import { Building } from 'app/data/types/building';
import { useBuilding } from 'app/shared/contexts/building';
import { useUser } from 'app/shared/contexts/user';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { DEFAULT_APP_BRAND } from 'app/shared/consts';

const useBrowser = () => useMemo(() => detect(), []);

const getAccountAnalytics = (building: Building): UserAnalytics['account'] => ({
  id: `${building.uuid}${process.env.REACT_APP_SENTRY_ID_SUFFIX ?? ''}`,
  current_building_uuid: building.uuid,
  current_building_name: building.name,
  current_portfolio_id: building.portfolio_uuid ?? '',
});

const getVisitorAnalytics = (
  accountId: string,
  building: Building,
  user: User,
  browser: ReturnType<typeof detect>,
  appBrand: string,
): UserAnalytics['visitor'] => ({
  id: `${user.uuid}${process.env.REACT_APP_SENTRY_ID_SUFFIX ?? ''}`,
  account: accountId,
  test_user: !!user.test,
  current_building_name: building.name,
  current_portfolio_id: building.portfolio_uuid ?? '',
  tenant_company_id: user.company_uuid ?? '',
  app_brand: appBrand ?? DEFAULT_APP_BRAND,
  current_user_roles: user.roles?.map(({ name }) => name) ?? [],
  os_name: browser?.name ?? '',
  os_version: browser?.version ?? '',
});

export const useSentryAnalytics = (): UserAnalytics | null => {
  const { building } = useBuilding();
  const { user } = useUser();
  const browser = useBrowser();
  const { appBrand } = useOpenParams();

  return useMemo(() => {
    if (!building || !user) {
      return null;
    }

    const account = getAccountAnalytics(building);
    const visitor = getVisitorAnalytics(account.id, building, user, browser, appBrand);

    return {
      visitor,
      account,
    };
  }, [building, user, browser, appBrand]);
};
